import styled from "@emotion/styled"
import { PHeading, PIcon, PText } from "@porsche-design-system/components-react"
import {
	colorContrastLow,
	fontWeightSemibold,
	leadingNormal,
	radiusMd,
	spacingStaticMd,
	spacingStaticSm,
	spacingStaticXs,
	typescaleLg,
} from "@porsche-design-system/components-react/emotion"
import type { NewsItemAdd } from "@slfinrtl/ui-bff-routes"
import { useCallback, useSyncExternalStore } from "react"
import { useFormContext } from "react-hook-form"
import { useIntl } from "react-intl"
import { Markdown } from "src/components"
import { remarkEmptyParagraphs } from "../remarkEmptyParagraphs"
import { remarkSupportedNodesOnly } from "../remarkSupportedNodesOnly"
import { newsEditorMessages as messages } from "./newsEditor.messages"

interface NewsPreviewProps {
	/** preview url of the first image */
	heroImageUrl?: string
}

// Fields with shouldUnregister restore their value without notifying the watchers when they are mounted again, e.g. by
// react strict mode. useWatch would keep the removed value then. An external store reads the value again after it has
// subscribed, so it picks up the restored one.
const useFormValue = (name: "content" | "plannedPublicationDate" | "title"): string => {
	const { getValues, watch } = useFormContext<NewsItemAdd>()
	const subscribe = useCallback(
		(onChange: () => void) => {
			const { unsubscribe } = watch(onChange)
			return unsubscribe
		},
		[watch],
	)
	return useSyncExternalStore(subscribe, () => getValues(name) ?? "")
}

export const NewsPreview = ({ heroImageUrl }: NewsPreviewProps) => {
	const { formatDate, formatMessage } = useIntl()
	const title = useFormValue("title")
	const content = useFormValue("content")
	const plannedPublicationDate = useFormValue("plannedPublicationDate")

	// the date is a plain day without a time zone, so it must not be shifted by the local one
	const plannedDate = new Date(plannedPublicationDate)
	const formattedDate = Number.isNaN(plannedDate.getTime())
		? undefined
		: formatDate(plannedDate, {
				day: "numeric",
				month: "numeric",
				timeZone: "UTC",
				weekday: "long",
				year: "numeric",
			})

	return (
		<Preview>
			<Label>
				<PIcon name="view" aria-hidden="true" />
				<PText size="sm" color="contrast-medium">
					{formatMessage(messages.livePreview)}
				</PText>
			</Label>
			{(title || formattedDate) && (
				<Headline>
					{title && (
						<PHeading data-testid="news-preview-headline" size="lg" tag="h2">
							{title}
						</PHeading>
					)}
					{formattedDate && (
						<PText size="sm" color="contrast-medium">
							{formattedDate}
						</PText>
					)}
				</Headline>
			)}
			{content && (
				<MarkdownContent>
					<Markdown remarkPlugins={[remarkEmptyParagraphs, remarkSupportedNodesOnly]} value={content} />
				</MarkdownContent>
			)}
			{heroImageUrl && <HeroImage alt="" data-testid="news-preview-image" src={heroImageUrl} />}
		</Preview>
	)
}

const Preview = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticMd};
`

const Headline = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticXs};
`

const Label = styled.div`
	display: flex;
	align-items: center;
	gap: ${spacingStaticXs};
	padding-block-end: ${spacingStaticSm};
	border-block-end: 1px solid ${colorContrastLow};
`

const HeroImage = styled.img`
	width: 100%;
	height: auto;
	border-radius: ${radiusMd};
`

// the spacings prop of the markdown component would use ":first-child", which emotion flags as unsafe for server-side rendering
const MarkdownContent = styled.div`
	/* as large as in the editor, the doubled class wins over the fluid size of the markdown component */
	&& h2 {
		font-size: ${typescaleLg};
		font-weight: ${fontWeightSemibold};
		line-height: ${leadingNormal};
	}

	/* the explicit parent and the direct children make it win over the margin reset of the markdown component */
	& > div > :is(p, h2, ul, ol) + :is(p, h2, ul, ol) {
		margin-top: ${spacingStaticSm};
	}
`
